import React, { useState, useEffect } from 'react';
import { Table, Input, Button, Space, Tag, Typography, Card, message, Modal, Form, Select, InputNumber, DatePicker, Tooltip, Popconfirm } from 'antd';
import { SearchOutlined, PlusOutlined, EyeOutlined, CopyOutlined, CheckCircleOutlined, CloseCircleOutlined, ReloadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';
import styled from 'styled-components';
import dayjs from 'dayjs';

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
  hover: '#1f2a37',
};

const { Title, Text } = Typography;
const { TextArea } = Input;

const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};
  
  @media (max-width: 768px) {
    padding: 0 8px;
  }
`;

const HeaderContainer = styled.div`
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h2 {
    margin-bottom: 0 !important;
    font-size: 24px !important;
    font-weight: 600 !important;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    
    h2 {
      font-size: 20px !important;
    }
  }
`;

const FiltersContainer = styled(Card)`
  margin-bottom: 24px;
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  
  .ant-card-body {
    padding: 20px !important;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    
    .ant-card-body {
      padding: 12px !important;
    }
  }
`;

const TableScrollWrapper = styled.div`
  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    margin: 0 -8px;
    padding: 0 8px;
  }
`;

const TokenCodeDisplay = styled.div`
  font-family: 'Courier New', monospace;
  font-weight: 600;
  color: ${COLORS.accent};
  letter-spacing: 1px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DarkModalStyles = styled.div`
  .dark-modal {
    .ant-modal-content {
      background: ${COLORS.bgSecondary} !important;
      border: 1px solid ${COLORS.border} !important;
    }
    
    .ant-modal-header {
      background: ${COLORS.bgSecondary} !important;
      border-bottom: 1px solid ${COLORS.border} !important;
    }
    
    .ant-modal-title {
      color: ${COLORS.textPrimary} !important;
    }
    
    .ant-modal-close {
      color: ${COLORS.textSecondary} !important;
      
      &:hover {
        color: ${COLORS.textPrimary} !important;
      }
    }
    
    .ant-modal-body {
      background: ${COLORS.bgSecondary} !important;
      color: ${COLORS.textPrimary} !important;
    }
    
    .ant-modal-footer {
      background: ${COLORS.bgSecondary} !important;
      border-top: 1px solid ${COLORS.border} !important;
    }
    
    .ant-form-item-label > label {
      color: ${COLORS.textPrimary} !important;
    }
    
    .ant-form-item-explain {
      color: ${COLORS.textSecondary} !important;
    }
    
    .ant-input,
    .ant-input-number,
    .ant-input-number-input,
    .ant-picker,
    .ant-select-selector {
      background-color: ${COLORS.bgPrimary} !important;
      border-color: ${COLORS.border} !important;
      color: ${COLORS.textPrimary} !important;
    }
    
    .ant-input::placeholder,
    .ant-input-number-input::placeholder,
    .ant-picker-input > input::placeholder {
      color: ${COLORS.textSecondary} !important;
    }
    
    .ant-select-selection-placeholder {
      color: ${COLORS.textSecondary} !important;
    }
    
    .ant-input-number-handler-wrap {
      background: ${COLORS.bgSecondary} !important;
      border-color: ${COLORS.border} !important;
    }
    
    .ant-input-number-handler {
      border-color: ${COLORS.border} !important;
      
      &:hover {
        background: ${COLORS.hover} !important;
      }
    }
    
    .ant-input-number-handler-up-inner,
    .ant-input-number-handler-down-inner {
      color: ${COLORS.textSecondary} !important;
    }
    
    .ant-picker-suffix,
    .ant-picker-clear {
      color: ${COLORS.textSecondary} !important;
    }
    
    .ant-select-arrow {
      color: ${COLORS.textSecondary} !important;
    }
    
    .ant-btn-default {
      background: ${COLORS.bgPrimary} !important;
      border-color: ${COLORS.border} !important;
      color: ${COLORS.textPrimary} !important;
      
      &:hover {
        border-color: ${COLORS.accent} !important;
        color: ${COLORS.accent} !important;
      }
    }
  }
`;

const CreditTokens: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [tokens, setTokens] = useState<any[]>([]);
  const [pagination, setPagination] = useState({
    current: Number(searchParams.get('pageId')) || 1,
    pageSize: Number(searchParams.get('perPage')) || 10,
    total: 0,
  });
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [modalVisible, setModalVisible] = useState(false);
  const [viewTokenModalVisible, setViewTokenModalVisible] = useState(false);
  const [selectedToken, setSelectedToken] = useState<any>(null);
  const [visibleTokenCodes, setVisibleTokenCodes] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState({
    isActive: searchParams.get('isActive') || undefined,
    creditType: searchParams.get('creditType') || undefined,
  });

  useEffect(() => {
    fetchTokens();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize, search, filters]);

  const fetchTokens = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        pageId: pagination.current.toString(),
        perPage: pagination.pageSize.toString(),
        ...(search && { search }),
        ...(filters.isActive !== undefined && { isActive: filters.isActive }),
        ...(filters.creditType && { creditType: filters.creditType }),
      });

      const response = await apiClient.get(`${API_ENDPOINTS.ADMIN_GET_CREDIT_TOKENS || '/api/Administrator/credit-tokens'}?${params}`);
      
      if (response.data?.status === 'success' && response.data?.data) {
        const data = response.data.data;
        const tokensList = data.tokens || data.itemsList || (Array.isArray(data) ? data : []);
        setTokens(tokensList);
        setPagination(prev => ({
          ...prev,
          total: data.pagination?.total || data.total || tokensList.length,
        }));
      }
    } catch (error: any) {
      message.error('Failed to fetch credit tokens');
      console.error('Error fetching credit tokens:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setSearch(value);
    setPagination(prev => ({ ...prev, current: 1 }));
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set('search', value);
    } else {
      newParams.delete('search');
    }
    newParams.set('pageId', '1');
    setSearchParams(newParams);
  };

  const handleCreate = () => {
    form.resetFields();
    setModalVisible(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        amount: values.amount,
        creditType: values.creditType,
        description: values.description,
        expiresAt: values.expiresAt ? (values.expiresAt.format ? values.expiresAt.format('YYYY-MM-DDTHH:mm:ss[Z]') : (typeof values.expiresAt === 'string' ? values.expiresAt : new Date(values.expiresAt).toISOString())) : undefined,
        maxRedemptions: values.maxRedemptions || 1,
        tokenCode: values.tokenCode, // Optional custom code
        prefix: values.prefix, // Optional prefix for generated token code
      };

      const response = await apiClient.post(
        API_ENDPOINTS.ADMIN_CREATE_CREDIT_TOKEN || '/api/Administrator/credit-tokens/create',
        payload
      );

      if (response.data?.status === 'success') {
        message.success('Credit token created successfully');
        setModalVisible(false);
        form.resetFields();
        fetchTokens();
        
        // Show the token code in a modal
        if (response.data?.data?.tokenCode) {
          setSelectedToken(response.data.data);
          setViewTokenModalVisible(true);
          setVisibleTokenCodes(new Set([response.data.data.id]));
        }
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to create credit token');
    }
  };

  const handleViewToken = (token: any) => {
    setSelectedToken(token);
    setViewTokenModalVisible(true);
    setVisibleTokenCodes(prev => {
      const newSet = new Set(prev);
      newSet.add(token.id);
      return newSet;
    });
  };

  const handleCopyToken = (tokenCode: string) => {
    navigator.clipboard.writeText(tokenCode);
    message.success('Token code copied to clipboard!');
  };

  const handleToggleStatus = async (tokenId: string, isActive: boolean) => {
    try {
      const endpoint = isActive
        ? API_ENDPOINTS.ADMIN_ACTIVATE_CREDIT_TOKEN || `/api/Administrator/credit-tokens/${tokenId}/activate`
        : API_ENDPOINTS.ADMIN_DEACTIVATE_CREDIT_TOKEN || `/api/Administrator/credit-tokens/${tokenId}/deactivate`;
      
      await apiClient.post(endpoint);
      message.success(`Token ${isActive ? 'activated' : 'deactivated'} successfully`);
      fetchTokens();
    } catch (error: any) {
      message.error(error.response?.data?.message || `Failed to ${isActive ? 'activate' : 'deactivate'} token`);
    }
  };

  const isTokenVisible = (tokenId: string) => visibleTokenCodes.has(tokenId);

  const maskTokenCode = (code: string) => {
    if (!code) return 'N/A';
    const parts = code.split('-');
    if (parts.length > 1) {
      return `${parts[0]}-****-****-****`;
    }
    return '****';
  };

  const columns = [
    {
      title: 'Token Code',
      key: 'tokenCode',
      render: (_: any, record: any) => (
        <TokenCodeDisplay>
          {isTokenVisible(record.id) ? (
            <>
              {record.tokenCode}
              <Tooltip title="Copy token code">
                <Button
                  type="text"
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => handleCopyToken(record.tokenCode)}
                  style={{ color: COLORS.accent }}
                />
              </Tooltip>
            </>
          ) : (
            <>
              {maskTokenCode(record.tokenCode)}
              <Tooltip title="View token code (shown once)">
                <Button
                  type="text"
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={() => handleViewToken(record)}
                  style={{ color: COLORS.accent }}
                />
              </Tooltip>
            </>
          )}
        </TokenCodeDisplay>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number) => `$${parseFloat(amount.toString()).toFixed(2)}`,
      sorter: (a: any, b: any) => parseFloat(a.amount.toString()) - parseFloat(b.amount.toString()),
    },
    {
      title: 'Type',
      dataIndex: 'creditType',
      key: 'creditType',
      render: (type: string) => {
        const colors: Record<string, string> = {
          PROMOTIONAL: 'blue',
          PAID: 'green',
          REFUND: 'orange',
          ADJUSTMENT: 'purple',
        };
        return <Tag color={colors[type] || 'default'}>{type}</Tag>;
      },
      filters: [
        { text: 'Promotional', value: 'PROMOTIONAL' },
        { text: 'Paid', value: 'PAID' },
        { text: 'Refund', value: 'REFUND' },
        { text: 'Adjustment', value: 'ADJUSTMENT' },
      ],
    },
    {
      title: 'Status',
      key: 'status',
      render: (_: any, record: any) => {
        const isExpired = record.expiresAt && new Date(record.expiresAt) < new Date();
        const isMaxed = record.currentRedemptions >= record.maxRedemptions;
        
        if (isExpired) {
          return <Tag color="red">Expired</Tag>;
        }
        if (isMaxed) {
          return <Tag color="orange">Maxed Out</Tag>;
        }
        if (record.isActive) {
          return <Tag color="green" icon={<CheckCircleOutlined />}>Active</Tag>;
        }
        return <Tag color="default" icon={<CloseCircleOutlined />}>Inactive</Tag>;
      },
      filters: [
        { text: 'Active', value: 'active' },
        { text: 'Inactive', value: 'inactive' },
        { text: 'Expired', value: 'expired' },
      ],
    },
    {
      title: 'Redemptions',
      key: 'redemptions',
      render: (_: any, record: any) => `${record.currentRedemptions} / ${record.maxRedemptions}`,
    },
    {
      title: 'Expires At',
      dataIndex: 'expiresAt',
      key: 'expiresAt',
      render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD') : 'Never',
    },
    {
      title: 'Created By',
      key: 'createdBy',
      render: (_: any, record: any) => {
        if (record.createdByAdmin) {
          return (
            <Tooltip title={record.createdByAdmin.email}>
              {record.createdByAdmin.firstName} {record.createdByAdmin.lastName}
            </Tooltip>
          );
        }
        return record.createdBy || '-';
      },
    },
    {
      title: 'Redeemed By',
      key: 'redeemedBy',
      render: (_: any, record: any) => {
        if (record.redeemedByMerchant) {
          return (
            <Tooltip title={record.redeemedByMerchant.companyEmail}>
              {record.redeemedByMerchant.name}
            </Tooltip>
          );
        }
        return '-';
      },
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm'),
      sorter: (a: any, b: any) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space>
          <Tooltip title="View token code">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewToken(record)}
            />
          </Tooltip>
          {record.isActive ? (
            <Popconfirm
              title="Deactivate this token?"
              onConfirm={() => handleToggleStatus(record.id, false)}
            >
              <Button type="text" size="small" danger icon={<CloseCircleOutlined />} />
            </Popconfirm>
          ) : (
            <Popconfirm
              title="Activate this token?"
              onConfirm={() => handleToggleStatus(record.id, true)}
            >
              <Button type="text" size="small" icon={<CheckCircleOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <DarkModalStyles>
    <PageContainer>
      <HeaderContainer>
        <Title level={2} style={{ color: COLORS.textPrimary, margin: 0 }}>
          Credit Tokens
        </Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
          style={{ backgroundColor: COLORS.accent, borderColor: COLORS.accent }}
        >
          Create Token
        </Button>
      </HeaderContainer>

      <FiltersContainer>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Space wrap>
            <Input
              placeholder="Search by token code..."
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              style={{ width: 250 }}
              allowClear
            />
            <Select
              placeholder="Status"
              style={{ width: 150 }}
              value={filters.isActive}
              onChange={(value) => {
                setFilters(prev => ({ ...prev, isActive: value }));
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              allowClear
            >
              <Select.Option value="true">Active</Select.Option>
              <Select.Option value="false">Inactive</Select.Option>
            </Select>
            <Select
              placeholder="Credit Type"
              style={{ width: 150 }}
              value={filters.creditType}
              onChange={(value) => {
                setFilters(prev => ({ ...prev, creditType: value }));
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              allowClear
            >
              <Select.Option value="PROMOTIONAL">Promotional</Select.Option>
              <Select.Option value="PAID">Paid</Select.Option>
              <Select.Option value="REFUND">Refund</Select.Option>
              <Select.Option value="ADJUSTMENT">Adjustment</Select.Option>
            </Select>
            <Button icon={<ReloadOutlined />} onClick={fetchTokens}>
              Refresh
            </Button>
          </Space>
        </Space>
      </FiltersContainer>

      <TableScrollWrapper>
        <Table
          columns={columns}
          dataSource={tokens}
          loading={loading}
          rowKey="id"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} tokens`,
            onChange: (page, pageSize) => {
              setPagination(prev => ({ ...prev, current: page, pageSize }));
              const newParams = new URLSearchParams(searchParams);
              newParams.set('pageId', page.toString());
              newParams.set('perPage', pageSize.toString());
              setSearchParams(newParams);
            },
          }}
          style={{ background: COLORS.bgSecondary }}
        />
      </TableScrollWrapper>

      {/* Create Token Modal */}
      <Modal
        title={<span style={{ color: COLORS.textPrimary }}>Create Credit Token</span>}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        width={600}
        okText="Create Token"
        cancelText="Cancel"
        okButtonProps={{ style: { backgroundColor: COLORS.accent, borderColor: COLORS.accent } }}
        bodyStyle={{ background: COLORS.bgSecondary, padding: '24px' }}
        className="dark-modal"
        style={{ top: 20 }}
      >
        <Form 
          form={form} 
          layout="vertical"
          style={{ color: COLORS.textPrimary }}
        >
          <Form.Item
            name="amount"
            label={
              <Space>
                <span style={{ color: COLORS.textPrimary }}>Credit Amount (USD)</span>
                <Tooltip title="The dollar amount that will be added to the merchant's credit balance when they redeem this token.">
                  <InfoCircleOutlined style={{ color: COLORS.textSecondary, cursor: 'help' }} />
                </Tooltip>
              </Space>
            }
            rules={[{ required: true, message: 'Please enter credit amount' }]}
          >
            <InputNumber
              style={{ 
                width: '100%', 
                backgroundColor: COLORS.bgPrimary, 
                borderColor: COLORS.border,
                color: COLORS.textPrimary,
              }}
              min={0.01}
              step={0.01}
              precision={2}
              placeholder="0.00"
              prefix="$"
            />
          </Form.Item>
          <Form.Item
            name="creditType"
            label={
              <Space>
                <span style={{ color: COLORS.textPrimary }}>Credit Type</span>
                <Tooltip 
                  title={
                    <div>
                      <div><strong>Promotional:</strong> Free credits for marketing campaigns</div>
                      <div><strong>Paid:</strong> Credits purchased by the merchant</div>
                      <div><strong>Refund:</strong> Credits issued as compensation</div>
                      <div><strong>Adjustment:</strong> Manual balance corrections</div>
                    </div>
                  }
                >
                  <InfoCircleOutlined style={{ color: COLORS.textSecondary, cursor: 'help' }} />
                </Tooltip>
              </Space>
            }
            rules={[{ required: true, message: 'Please select credit type' }]}
          >
            <Select 
              placeholder="Select credit type"
              style={{ backgroundColor: COLORS.bgPrimary }}
              dropdownStyle={{ backgroundColor: COLORS.bgSecondary }}
            >
              <Select.Option value="PROMOTIONAL">Promotional</Select.Option>
              <Select.Option value="PAID">Paid</Select.Option>
              <Select.Option value="REFUND">Refund</Select.Option>
              <Select.Option value="ADJUSTMENT">Adjustment</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="description"
            label={<span style={{ color: COLORS.textPrimary }}>Description (Optional)</span>}
          >
            <TextArea 
              rows={3} 
              placeholder="Optional description for this token (e.g., 'Welcome bonus for new merchant')" 
              style={{ 
                backgroundColor: COLORS.bgPrimary, 
                borderColor: COLORS.border, 
                color: COLORS.textPrimary,
              }}
            />
          </Form.Item>
          <Form.Item
            name="expiresAt"
            label={
              <Space>
                <span style={{ color: COLORS.textPrimary }}>Expiration Date (Optional)</span>
                <Tooltip title="If set, the token cannot be redeemed after this date. Leave empty for tokens that never expire.">
                  <InfoCircleOutlined style={{ color: COLORS.textSecondary, cursor: 'help' }} />
                </Tooltip>
              </Space>
            }
          >
            <DatePicker
              style={{ 
                width: '100%', 
                backgroundColor: COLORS.bgPrimary, 
                borderColor: COLORS.border,
              }}
              showTime
              format="YYYY-MM-DD HH:mm"
              placeholder="Select expiration date and time"
            />
          </Form.Item>
          <Form.Item
            name="maxRedemptions"
            label={
              <Space>
                <span style={{ color: COLORS.textPrimary }}>Max Redemptions</span>
                <Tooltip 
                  title={
                    <div>
                      <div>The maximum number of times this token can be used.</div>
                      <div style={{ marginTop: 8 }}><strong>Example:</strong></div>
                      <div>• Set to <strong>1</strong> for a single-use token (one merchant can redeem it once)</div>
                      <div>• Set to <strong>10</strong> to allow 10 different merchants to redeem the same code</div>
                      <div style={{ marginTop: 8, color: '#F59E0B' }}>Once max redemptions is reached, the token becomes invalid.</div>
                    </div>
                  }
                >
                  <InfoCircleOutlined style={{ color: COLORS.textSecondary, cursor: 'help' }} />
                </Tooltip>
              </Space>
            }
            initialValue={1}
            rules={[{ required: true, message: 'Please enter max redemptions' }]}
          >
            <InputNumber
              style={{ 
                width: '100%', 
                backgroundColor: COLORS.bgPrimary, 
                borderColor: COLORS.border,
                color: COLORS.textPrimary,
              }}
              min={1}
              placeholder="1"
            />
          </Form.Item>
          <Form.Item
            name="prefix"
            label={
              <Space>
                <span style={{ color: COLORS.textPrimary }}>Token Prefix (Optional)</span>
                <Tooltip title="Custom prefix for the auto-generated token code. Useful for organizing tokens by campaign or purpose (e.g., 'WELCOME', 'PROMO2024').">
                  <InfoCircleOutlined style={{ color: COLORS.textSecondary, cursor: 'help' }} />
                </Tooltip>
              </Space>
            }
            help={<span style={{ color: COLORS.textSecondary }}>Default prefix is 'CREDIT'. Example output: BONUS-A1B2-C3D4-E5F6</span>}
          >
            <Input
              placeholder="e.g., BONUS, PROMO, REFUND"
              style={{ 
                textTransform: 'uppercase', 
                backgroundColor: COLORS.bgPrimary, 
                borderColor: COLORS.border,
                color: COLORS.textPrimary,
              }}
              maxLength={20}
            />
          </Form.Item>
          <Form.Item
            name="tokenCode"
            label={
              <Space>
                <span style={{ color: COLORS.textPrimary }}>Custom Token Code (Optional)</span>
                <Tooltip title="Specify your own token code instead of auto-generating one. Must be unique across all tokens. Overrides the prefix setting.">
                  <InfoCircleOutlined style={{ color: COLORS.textSecondary, cursor: 'help' }} />
                </Tooltip>
              </Space>
            }
            help={<span style={{ color: COLORS.textSecondary }}>Leave empty to auto-generate. Format: PREFIX-XXXX-XXXX-XXXX</span>}
          >
            <Input 
              placeholder="PREFIX-XXXX-XXXX-XXXX" 
              style={{ 
                fontFamily: 'monospace', 
                textTransform: 'uppercase',
                backgroundColor: COLORS.bgPrimary, 
                borderColor: COLORS.border,
                color: COLORS.textPrimary,
              }} 
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* View Token Modal */}
      <Modal
        title={<span style={{ color: COLORS.textPrimary }}>Credit Token Details</span>}
        open={viewTokenModalVisible}
        onCancel={() => {
          setViewTokenModalVisible(false);
          setSelectedToken(null);
        }}
        footer={
          <Space>
            <Button 
              key="copy" 
              type="primary"
              icon={<CopyOutlined />} 
              onClick={() => selectedToken && handleCopyToken(selectedToken.tokenCode)}
              style={{ backgroundColor: COLORS.accent, borderColor: COLORS.accent }}
            >
              Copy Token Code
            </Button>
            <Button key="close" onClick={() => {
              setViewTokenModalVisible(false);
              setSelectedToken(null);
            }}>
              Close
            </Button>
          </Space>
        }
        width={500}
        bodyStyle={{ background: COLORS.bgSecondary, padding: '24px' }}
        className="dark-modal"
        style={{ top: 20 }}
      >
        {selectedToken && (
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <div>
              <Text style={{ color: COLORS.textSecondary }}>Token Code:</Text>
              <TokenCodeDisplay style={{ marginTop: 8, fontSize: '18px', padding: '12px', background: COLORS.bgPrimary, borderRadius: '8px', border: `1px solid ${COLORS.border}` }}>
                {selectedToken.tokenCode}
              </TokenCodeDisplay>
              <Text style={{ color: '#F59E0B', fontSize: '12px', display: 'block', marginTop: 8 }}>
                ⚠️ This code will be shown only once. Please copy it now.
              </Text>
            </div>
            <div>
              <Text style={{ color: COLORS.textSecondary }}>Amount:</Text>
              <div style={{ fontSize: '20px', fontWeight: 600, color: COLORS.accent, marginTop: 4 }}>
                ${parseFloat(selectedToken.amount.toString()).toFixed(2)}
              </div>
            </div>
            <div>
              <Text style={{ color: COLORS.textSecondary }}>Type:</Text>
              <div style={{ marginTop: 4 }}>
                <Tag color="blue">{selectedToken.creditType}</Tag>
              </div>
            </div>
            {selectedToken.description && (
              <div>
                <Text style={{ color: COLORS.textSecondary }}>Description:</Text>
                <div style={{ marginTop: 4, color: COLORS.textPrimary }}>{selectedToken.description}</div>
              </div>
            )}
            <div>
              <Text style={{ color: COLORS.textSecondary }}>Max Redemptions:</Text>
              <div style={{ marginTop: 4, color: COLORS.textPrimary }}>{selectedToken.maxRedemptions}</div>
            </div>
            {selectedToken.expiresAt && (
              <div>
                <Text style={{ color: COLORS.textSecondary }}>Expires At:</Text>
                <div style={{ marginTop: 4, color: COLORS.textPrimary }}>{dayjs(selectedToken.expiresAt).format('YYYY-MM-DD HH:mm')}</div>
              </div>
            )}
            {selectedToken.createdByAdmin && (
              <div>
                <Text style={{ color: COLORS.textSecondary }}>Created By:</Text>
                <div style={{ marginTop: 4, color: COLORS.textPrimary }}>
                  {selectedToken.createdByAdmin.firstName} {selectedToken.createdByAdmin.lastName}
                  <Text style={{ marginLeft: 8, fontSize: '12px', color: COLORS.textSecondary }}>
                    ({selectedToken.createdByAdmin.email})
                  </Text>
                </div>
              </div>
            )}
          </Space>
        )}
      </Modal>
    </PageContainer>
    </DarkModalStyles>
  );
};

export default CreditTokens;
